import { notificationsAPI } from "@/lib/api";
import {
  FrontendNotification,
  fallbackModuleRouteForNotificationType,
  getDynamicNotifications,
  getStockAlertNotifications,
} from "@/lib/notifications";
import type { SessionModuleAccess } from "@/lib/modulePermissions";

export type DisplayNotification = FrontendNotification & { isPersistent?: boolean };

interface NotificationsState {
  items: DisplayNotification[];
  loading: boolean;
  lastLoadedAt: number | null;
}

const state: NotificationsState = {
  items: [],
  loading: false,
  lastLoadedAt: null,
};

const listeners = new Set<() => void>();
let inFlight: Promise<void> | null = null;
let intervalId: ReturnType<typeof setInterval> | null = null;
let pollingUserId: number | undefined;
let getFacturasSnapshot: (() => any[]) | null = null;
let getModuleAccess: (() => SessionModuleAccess | null) | null = null;

const emit = () => {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error("notificationsLiveStore listener error:", error);
    }
  });
};

const setState = (partial: Partial<NotificationsState>) => {
  Object.assign(state, partial);
  emit();
};

const mergeAndSort = (dynamic: FrontendNotification[], stockAlerts: FrontendNotification[], apiNotes: any[]): DisplayNotification[] => {
  const rawNotes = Array.isArray(apiNotes) ? apiNotes : [];
  const mappedApi: DisplayNotification[] = rawNotes.map((n: any) => {
    const rawRoute = n.module_route ?? n.moduleRoute;
    const ts = n.created_at ?? n.createdAt ?? new Date().toISOString();
    return {
      id: String(n.id),
      type: n.type,
      title: n.title,
      message: n.message,
      severity: n.severity,
      timestamp: ts,
      moduleRoute: rawRoute || fallbackModuleRouteForNotificationType(n.type),
      data: n.data,
      isPersistent: true,
    };
  });

  const merged: DisplayNotification[] = [
    ...dynamic.map((n) => ({ ...n, isPersistent: false })),
    ...stockAlerts.map((n) => ({ ...n, isPersistent: false })),
    ...mappedApi,
  ];

  merged.sort((a, b) => {
    const severityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
    const sa = severityOrder[a.severity] ?? 3;
    const sb = severityOrder[b.severity] ?? 3;
    if (sa !== sb) return sa - sb;
    const tb = new Date(b.timestamp).getTime();
    const ta = new Date(a.timestamp).getTime();
    return (Number.isFinite(tb) && Number.isFinite(ta) ? tb - ta : 0);
  });

  return merged;
};

export const notificationsLiveStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  getSnapshot(): NotificationsState {
    return { ...state, items: [...state.items] };
  },

  async refresh(userId?: number, ttlMs = 5000): Promise<void> {
    const now = Date.now();
    if (state.lastLoadedAt && now - state.lastLoadedAt < ttlMs) {
      return;
    }

    // Si ya hay un fetch en curso, esperarlo y volver a intentar (p. ej. vaciar stock + emit en el mismo tick).
    // Antes: return inFlight hacía que este refresh no trajera datos nuevos.
    if (inFlight) {
      try {
        await inFlight;
      } catch {
        /* el error ya se logueó en el fetch anterior */
      }
      const after = Date.now();
      if (ttlMs > 0 && state.lastLoadedAt && after - state.lastLoadedAt < ttlMs) {
        return;
      }
      return this.refresh(userId, ttlMs);
    }

    setState({ loading: true });
    inFlight = (async () => {
      try {
        const facturas = getFacturasSnapshot ? getFacturasSnapshot() : [];
        const access = getModuleAccess ? getModuleAccess() : null;
        const [dynamic, stockAlerts, apiNotes] = await Promise.all([
          getDynamicNotifications(facturas, access).catch((e) => {
            console.error("getDynamicNotifications failed:", e);
            return [] as FrontendNotification[];
          }),
          getStockAlertNotifications(access).catch((e) => {
            console.error("getStockAlertNotifications failed:", e);
            return [] as FrontendNotification[];
          }),
          notificationsAPI.list(userId, false).catch(() => []),
        ]);

        setState({
          items: mergeAndSort(dynamic, stockAlerts, apiNotes),
          loading: false,
          lastLoadedAt: Date.now(),
        });
      } catch (error) {
        console.error("notificationsLiveStore.refresh failed:", error);
        setState({ loading: false });
      } finally {
        inFlight = null;
      }
    })();

    return inFlight;
  },

  startPolling(
    userId: number | undefined,
    facturasSnapshotGetter: () => any[],
    moduleAccessGetter?: () => SessionModuleAccess | null,
  ) {
    pollingUserId = userId;
    getFacturasSnapshot = facturasSnapshotGetter;
    getModuleAccess = moduleAccessGetter ?? null;

    if (intervalId) {
      return;
    }

    void this.refresh(pollingUserId, 0);
    intervalId = setInterval(() => {
      void this.refresh(pollingUserId, 0);
    }, 30000);
  },

  stopPolling() {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    pollingUserId = undefined;
    getFacturasSnapshot = null;
    getModuleAccess = null;
  },
};

